'use client';

import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  MessageSquare,
  Calendar,
  CheckCircle,
  Pin,
  MoreVertical,
  PinOff,
  Trash2,
  Snowflake,
  Sun,
  Copy,
} from 'lucide-react';
import { useGetUserById } from '../../hooks/use-user';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCategories } from '../../hooks/use-categories';
import { useForum } from '../../hooks/use-forum';
import { toast } from 'sonner';
import { getDiscussionPath } from '../../utils/slug';
import { ShareButton } from '../feedback/share-button';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { TagBadgeList } from '@/components/ui/tag-badge';
import { getUserAvatar } from '../../config/media-defaults';

export function ForumDiscussionCard({
  discussion,
  onUpvote,
  onDownvote,
  category,
  categories = [], // Pass categories for category matching
  user,
  communitySlug: propCommunitySlug, // Allow passing community slug as prop
}) {
  // Get community slug from URL params or prop
  const { communitySlug: paramCommunitySlug } = useParams();
  const navigate = useNavigate();
  const communitySlug =
    propCommunitySlug || paramCommunitySlug || discussion?.communitySlug;

  const [mintDialogOpen, setMintDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: '',
    title: '',
    description: '',
    action: null,
  });

  // Fetch the discussion creator's profile
  const {
    data: author,
    isLoading: isLoadingCreator,
    error: creatorError,
  } = useGetUserById(discussion?.creator);

  // Match category by slug, fallback to name
  const matchedCategory =
    (categories || []).find(
      (cat) =>
        (cat.slug && cat.slug === discussion.category) ||
        (cat.name && cat.name === discussion.category)
    ) || category;

  // Check if discussion is pinned
  const isPinned = discussion?.pinned === true;

  // Check if discussion is frozen
  const isFrozen = discussion?.frozen === true;

  // Check if user is moderator or admin
  const canModerate = user?.role === 'moderator' || user?.role === 'admin';

  // Handle UAL copy
  const handleCopyUAL = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await navigator.clipboard.writeText(discussion.ual);
      toast.success('UAL copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy UAL');
    }
  };

  // Show confirmation dialog
  const showConfirmation = (type, title, description, action) => {
    setConfirmDialog({
      open: true,
      type,
      title,
      description,
      action,
    });
  };

  // Handle confirmation dialog close
  const handleConfirmClose = () => {
    setConfirmDialog({
      open: false,
      type: '',
      title: '',
      description: '',
      action: null,
    });
  };

  // Handle confirmation dialog confirm
  const handleConfirm = () => {
    if (confirmDialog.action) {
      confirmDialog.action();
    }
    handleConfirmClose();
  };

  const { pinDiscussion, unpinDiscussion } = useCategories();
  const { freezeDiscussion, unfreezeDiscussion, deleteDiscussion } = useForum();

  // Moderator action handlers
  const handlePinToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const isPinning = !discussion.pinned;
    const categoryToUse = matchedCategory || category;
    const pinData = {
      categoryId:
        categoryToUse?.id || categoryToUse?.name || discussion.category,
      name: categoryToUse?.name || discussion.category,
      discussionOid: discussion.oid,
    };

    const unpinData = {
      categoryId:
        categoryToUse?.id || categoryToUse?.name || discussion.category,
      name: categoryToUse?.name || discussion.category,
      discussionOid: discussion.oid,
    };

    const action = () => {
      if (isPinning) {
        pinDiscussion.mutate(pinData);
      } else {
        unpinDiscussion.mutate(unpinData);
      }
    };

    showConfirmation(
      isPinning ? 'pin' : 'unpin',
      isPinning ? 'Pin Discussion' : 'Unpin Discussion',
      isPinning
        ? `Are you sure you want to pin "${discussion.title}"? This will make it appear at the top of the discussion list.`
        : `Are you sure you want to unpin "${discussion.title}"? It will return to its normal position in the list.`,
      action
    );
  };

  const handleFreezeToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const isFreezing = !discussion.frozen;
    const action = () => {
      if (isFreezing) {
        freezeDiscussion().mutate(
          { discussionOid: discussion.oid },
          {
            onSuccess: () => {
              toast.success('Discussion frozen successfully');
            },
            onError: (err) => {
              toast.error('Failed to freeze discussion');
              console.error('Freeze error:', err);
            },
          }
        );
      } else {
        unfreezeDiscussion().mutate(
          { discussionOid: discussion.oid },
          {
            onSuccess: () => {
              toast.success('Discussion unfrozen successfully');
            },
            onError: (err) => {
              toast.error('Failed to unfreeze discussion');
              console.error('Unfreeze error:', err);
            },
          }
        );
      }
    };

    showConfirmation(
      isFreezing ? 'freeze' : 'unfreeze',
      isFreezing ? 'Freeze Discussion' : 'Unfreeze Discussion',
      isFreezing
        ? `Are you sure you want to freeze "${discussion.title}"? Users will no longer be able to reply to this discussion.`
        : `Are you sure you want to unfreeze "${discussion.title}"? Users will be able to reply again.`,
      action
    );
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const action = () => {
      deleteDiscussion().mutate(
        { discussionOid: discussion.oid },
        {
          onSuccess: () => {
            toast.success('Discussion deleted successfully');
          },
          onError: (err) => {
            toast.error('Failed to delete discussion');
            console.error('Delete error:', err);
          },
        }
      );
    };

    showConfirmation(
      'delete',
      'Delete Discussion',
      `Are you sure you want to permanently delete "${discussion.title}"? This action cannot be undone and will remove all comments and replies.`,
      action
    );
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -2, transition: { duration: 0.2 } }}
        className="h-full"
      >
        <Card
          className={`h-full border transition-all duration-300 overflow-hidden relative group ${
            isPinned && discussion.frozen
              ? 'bg-gradient-to-r from-amber-950/40 via-purple-950/40 to-blue-950/40 hover:from-amber-900/50 hover:via-purple-900/50 hover:to-blue-900/50 border-purple-900/30 hover:shadow-lg hover:shadow-purple-900/20'
              : isPinned
              ? 'bg-gradient-to-r from-amber-950/40 to-orange-950/40 hover:from-amber-900/50 hover:to-orange-900/50 border-amber-900/30 hover:shadow-lg hover:shadow-amber-900/20'
              : discussion.frozen
              ? 'bg-gradient-to-r from-blue-950/40 to-cyan-950/40 hover:from-blue-900/50 hover:to-cyan-900/50 border-blue-900/30 hover:shadow-lg hover:shadow-blue-900/20'
              : 'bg-zinc-900/50 backdrop-blur-sm border-white/5 hover:border-violet-500/20 hover:shadow-lg hover:shadow-violet-500/5'
          }`}
        >
          <Link
            to={getDiscussionPath(discussion, communitySlug)}
            className="p-5 h-full flex flex-col"
          >
            {/* Header: Badges */}
            <div className="flex flex-col gap-3 mb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Category Badge */}
                  {matchedCategory && (
                    <Badge
                      variant="outline"
                      className="border-white/10 rounded-full px-2.5 py-0.5 text-xs font-medium"
                      style={{
                        backgroundColor: `${matchedCategory.color}15`,
                        color: matchedCategory.color,
                        borderColor: `${matchedCategory.color}30`,
                      }}
                    >
                      {matchedCategory.name}
                    </Badge>
                  )}

                  {/* Pinned Badge */}
                  {isPinned && (
                    <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 hover:bg-amber-500/20 gap-1">
                      <Pin className="h-3 w-3" />
                      Pinned
                    </Badge>
                  )}

                  {/* Frozen Badge */}
                  {discussion.frozen && (
                    <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 hover:bg-blue-500/20 gap-1">
                      <Snowflake className="h-3 w-3" />
                      Frozen
                    </Badge>
                  )}

                  {/* Discussion Tags */}
                  {discussion.tags && discussion.tags.length > 0 && (
                    <TagBadgeList
                      tags={discussion.tags}
                      size="xs"
                      maxVisible={3}
                      showOverflow={true}
                      className="ml-1"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Title & Description */}
            <div className="mb-4 flex-grow">
              <h3
                className={`text-lg font-bold mb-2 line-clamp-2 leading-tight group-hover:text-violet-400 transition-colors ${
                  isPinned ? 'text-amber-100' : 'text-white'
                }`}
              >
                {discussion.title}
              </h3>
              <p className="text-sm text-zinc-400 line-clamp-2 leading-relaxed">
                {discussion.description ||
                  discussion.excerpt ||
                  'No description provided.'}
              </p>
            </div>

            {/* Footer: Stats, Date & Author */}
            <div className="flex items-center justify-between mt-auto pt-4 border-t border-white/5">
              <div className="flex items-center gap-4 text-xs text-zinc-500 font-medium flex-wrap">
                <span className="flex items-center gap-1.5" title="Views">
                  <div className="p-1.5 rounded-md bg-white/5 group-hover:bg-white/10 transition-colors">
                    <Sun className="h-3.5 w-3.5" />
                  </div>
                  {discussion.views || 0}
                </span>
                <span className="flex items-center gap-1.5" title="Replies">
                  <div className="p-1.5 rounded-md bg-white/5 group-hover:bg-white/10 transition-colors">
                    <MessageSquare className="h-3.5 w-3.5" />
                  </div>
                  {discussion.replies || 0}
                </span>
                <span className="flex items-center gap-1.5" title="Created">
                  <div className="p-1.5 rounded-md bg-white/5 group-hover:bg-white/10 transition-colors">
                    <Calendar className="h-3.5 w-3.5" />
                  </div>
                  {format(new Date(discussion.createdAt), 'MMM d')}
                </span>

                {/* Author Avatar (Moved to Footer) */}
                <div className="flex items-center gap-2 ml-2 pl-4 border-l border-white/10">
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <div
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/profile/${discussion?.creator}`);
                        }}
                        className="flex items-center gap-2 cursor-pointer group/author"
                      >
                        <Avatar className="h-6 w-6 border border-white/10 group-hover/author:border-violet-500/50 transition-colors">
                          <AvatarImage src={getUserAvatar(author)} />
                          <AvatarFallback className="bg-zinc-800 text-zinc-400 text-[10px]">
                            {author?.username?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-zinc-400 group-hover/author:text-violet-400 transition-colors">
                          {author?.username || 'Unknown'}
                        </span>
                      </div>
                    </HoverCardTrigger>
                    <HoverCardContent className="w-80 bg-zinc-900 border-zinc-800 text-white p-4">
                      <div
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/profile/${discussion?.creator}`);
                        }}
                        className="flex gap-4 hover:opacity-90 transition-opacity cursor-pointer"
                      >
                        <Avatar className="h-12 w-12 border border-zinc-700">
                          <AvatarImage src={getUserAvatar(author)} />
                          <AvatarFallback className="bg-zinc-800 text-zinc-400">
                            {author?.username?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="space-y-1">
                          <h4 className="text-sm font-semibold text-white hover:text-violet-400 transition-colors">
                            {author?.username || 'Unknown User'}
                          </h4>
                          {author?.bio && (
                            <p className="text-xs text-zinc-400 line-clamp-2">
                              {author.bio}
                            </p>
                          )}
                          <div className="flex items-center gap-3 pt-2 text-xs text-zinc-500">
                            <span>
                              <b className="text-white">
                                {author?.followers || 0}
                              </b>{' '}
                              Followers
                            </span>
                            <span>
                              <b className="text-white">
                                {author?.discussionsCreated || 0}
                              </b>{' '}
                              Posts
                            </span>
                          </div>
                        </div>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </div>
              </div>

              {/* UAL Copy (Compact) */}
              {discussion.ual && (
                <div
                  onClick={handleCopyUAL}
                  className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-violet-400 cursor-pointer transition-colors px-2 py-1 rounded-md hover:bg-violet-500/10 ml-auto"
                >
                  <Copy className="h-3 w-3" />
                  <span className="hidden sm:inline">Copy UAL</span>
                </div>
              )}
            </div>
          </Link>

          {/* Moderator Actions (Absolute Positioned) */}
          {canModerate && (
            <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 bg-black/50 hover:bg-black/70 text-white rounded-full backdrop-blur-sm"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="bg-zinc-900 border-zinc-800 text-white"
                >
                  <DropdownMenuItem
                    onClick={handlePinToggle}
                    className="cursor-pointer hover:bg-zinc-800"
                  >
                    {isPinned ? (
                      <>
                        <PinOff className="mr-2 h-4 w-4" /> Unpin
                      </>
                    ) : (
                      <>
                        <Pin className="mr-2 h-4 w-4" /> Pin
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleFreezeToggle}
                    className="cursor-pointer hover:bg-zinc-800"
                  >
                    {discussion.frozen ? (
                      <>
                        <Sun className="mr-2 h-4 w-4" /> Unfreeze
                      </>
                    ) : (
                      <>
                        <Snowflake className="mr-2 h-4 w-4" /> Freeze
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem
                    onClick={handleDelete}
                    className="text-red-400 cursor-pointer hover:bg-red-900/20"
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </Card>
      </motion.div>

      <AlertDialog open={confirmDialog.open} onOpenChange={handleConfirmClose}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {confirmDialog.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {confirmDialog.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleConfirmClose}
              className="bg-transparent border-zinc-700 text-white hover:bg-zinc-800"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className={`${
                confirmDialog.type === 'delete'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-purple-600 hover:bg-purple-700'
              } text-white border-0`}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
